import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { FilterViewer, SearchFilter } from "./filterRegistry";
import { createdRangeValue } from "./createdFilter";
import { formatValue, spliceText, tokenize } from "./searchTokens";
import { matchingFilters, suggestionContext } from "./suggestions";
import { FilterOption, narrowOptions, useFilterOptions } from "./useFilterOptions";

export type Suggestion =
  | { kind: "key"; filter: SearchFilter }
  | { kind: "value"; option: FilterOption };

interface UseSearchAutocompleteArgs {
  raw: string;
  onRawChange: (raw: string) => void;
  /** Sends the search right away (Enter). */
  onSubmit: (raw: string) => void;
  viewer: FilterViewer;
  /** Touch layout: the key list waits for typing. */
  coarse: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
}

/**
 * Combobox state for the search bar: which list the caret calls for, the
 * active option, keyboard handling, and the string safe to send to the API
 * (what is being typed into an open list is held back).
 */
export function useSearchAutocomplete({
  raw,
  onRawChange,
  onSubmit,
  viewer,
  coarse,
  inputRef,
}: UseSearchAutocompleteArgs) {
  const [caret, setCaret] = useState(raw.length);
  const [focused, setFocused] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [active, setActive] = useState({ signature: "", index: -1 });
  const [calendarFor, setCalendarFor] = useState<number | null>(null);
  // The caret position of the last keystroke. Only the value part typed into is held
  // back, and only while the caret stays in it: never a filter the caret visits.
  const [typedAt, setTypedAt] = useState<number | null>(null);
  const pendingCaret = useRef<number | null>(null);

  const tokens = useMemo(() => tokenize(raw), [raw]);
  const context = useMemo(() => suggestionContext(raw, tokens, caret), [raw, tokens, caret]);
  const valueFilter = context?.kind === "value" ? context.filter : undefined;
  const { options: allOptions, hasMore } = useFilterOptions(
    valueFilter,
    context?.kind === "value" ? context.query : ""
  );

  const exactValue =
    context?.kind === "value" &&
    allOptions.some(
      (option) =>
        !option.opensCalendar && option.value.toLowerCase() === context.query.toLowerCase()
    );

  const suggestions: Suggestion[] = useMemo(() => {
    if (!context) return [];
    if (context.kind === "key") {
      return matchingFilters(viewer, context.query).map((filter) => ({ kind: "key", filter }));
    }
    // A finished value shows the whole list, so clicking into it offers a swap.
    const query = exactValue ? "" : context.query;
    return narrowOptions(allOptions, query, context.filter.match, context.taken).map((option) => ({
      kind: "value",
      option,
    }));
  }, [context, viewer, allOptions, exactValue]);

  const calendarOpen = context?.kind === "value" && calendarFor === context.token.from;
  const waitsForTyping = coarse && context?.kind === "key" && context.query === "";
  const open =
    focused &&
    !dismissed &&
    context !== null &&
    !waitsForTyping &&
    (calendarOpen || suggestions.length > 0);

  const signature = context ? `${context.kind}:${context.from}:${context.query}` : "";
  const activeIndex =
    open && active.signature === signature && active.index < suggestions.length ? active.index : -1;

  const typedPart = typedAt === null ? null : suggestionContext(raw, tokens, typedAt);
  const inTypedPart =
    context !== null && typedPart?.kind === context.kind && typedPart.from === context.from;
  if (typedAt !== null && !inTypedPart) setTypedAt(null);
  // What is being typed into the open list stays out of the API: an unfinished value, or
  // a free-text word that still matches keys. Anything else goes out as typed.
  const typingIntoList = open && inTypedPart && context.query !== "";
  let heldSpan: { from: number; to: number } | null = null;
  if (typingIntoList && context.kind === "key") heldSpan = context.token ?? null;
  if (typingIntoList && context.kind === "value" && !exactValue) heldSpan = context;
  const committedRaw = heldSpan ? raw.slice(0, heldSpan.from) + raw.slice(heldSpan.to) : raw;

  // Selection-only moves (a touch caret-handle drag) may fire only selectionchange, which
  // React's onSelect misses: on the input in Chromium/Firefox, on the document in Safari.
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    const syncFocusedCaret = () => {
      if (document.activeElement === input) setCaret(input.selectionStart ?? input.value.length);
    };
    input.addEventListener("selectionchange", syncFocusedCaret);
    document.addEventListener("selectionchange", syncFocusedCaret);
    return () => {
      input.removeEventListener("selectionchange", syncFocusedCaret);
      document.removeEventListener("selectionchange", syncFocusedCaret);
    };
  }, [inputRef]);

  useLayoutEffect(() => {
    const input = inputRef.current;
    if (input && pendingCaret.current !== null) {
      input.setSelectionRange(pendingCaret.current, pendingCaret.current);
      pendingCaret.current = null;
    }
  }, [raw, inputRef]);

  const replace = useCallback(
    (from: number, to: number, text: string) => {
      const next = spliceText(raw, from, to, text);
      setCaret(next.caret);
      setTypedAt(null);
      if (next.raw === raw) {
        // Nothing re-renders the text, so the caret moves now.
        inputRef.current?.setSelectionRange(next.caret, next.caret);
        return;
      }
      pendingCaret.current = next.caret;
      onRawChange(next.raw);
    },
    [raw, onRawChange, inputRef]
  );

  const accept = useCallback(
    (suggestion: Suggestion) => {
      if (!context) return;
      if (suggestion.kind === "key") {
        replace(context.from, context.to, `${suggestion.filter.key}:`);
        setDismissed(false);
        return;
      }
      if (context.kind !== "value") return;
      if (suggestion.option.opensCalendar) {
        setCalendarFor(context.token.from);
        return;
      }
      replace(context.from, context.to, formatValue(suggestion.option.value));
      setDismissed(true);
    },
    [context, replace]
  );

  const acceptRange = useCallback(
    (range: { from: Date; to?: Date }) => {
      if (context?.kind !== "value") return;
      replace(context.token.valueFrom, context.token.valueTo, createdRangeValue(range));
      setCalendarFor(null);
      setDismissed(true);
    },
    [context, replace]
  );

  const moveActive = (step: 1 | -1) => {
    const count = suggestions.length;
    const start = activeIndex === -1 ? (step === 1 ? -1 : count) : activeIndex;
    setActive({ signature, index: (start + step + count) % count });
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing) return;
    const listOpen = open && !calendarOpen;
    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp":
        if (!listOpen) return;
        event.preventDefault();
        moveActive(event.key === "ArrowDown" ? 1 : -1);
        return;
      case "Tab": {
        // With nothing chosen or typed (or a finished value), Tab moves focus on.
        const takesFirst = context?.query !== "" && !exactValue;
        if (!listOpen || event.shiftKey || (activeIndex < 0 && !takesFirst)) return;
        event.preventDefault();
        accept(suggestions[Math.max(activeIndex, 0)]);
        return;
      }
      case "Enter":
        event.preventDefault();
        if (listOpen && activeIndex >= 0) {
          accept(suggestions[activeIndex]);
        } else if (listOpen && context?.kind === "value" && !exactValue) {
          // An unfinished value is not searched: Enter steps into its options instead.
          setActive({ signature, index: 0 });
        } else {
          setDismissed(true);
          onSubmit(raw);
        }
        return;
      case "Escape":
        // Also before async options arrive, so a typed value is released at once.
        if (!context) return;
        if (open) event.preventDefault();
        setCalendarFor(null);
        setDismissed(true);
        setTypedAt(null);
        return;
    }
  };

  const syncCaret = (input: HTMLInputElement) =>
    setCaret(input.selectionStart ?? input.value.length);

  const inputProps = {
    onKeyDown,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      syncCaret(event.target);
      setDismissed(false);
      // Any edit starts the list over: retyping the same text must not revive a stale row.
      setActive({ signature: "", index: -1 });
      // Only a keystroke into a value part or a free-text word can be held back.
      const at = event.target.selectionStart ?? event.target.value.length;
      const typed = suggestionContext(event.target.value, tokenize(event.target.value), at);
      const holdable =
        typed?.kind === "value" || (typed?.kind === "key" && typed.token?.key === null);
      setTypedAt(holdable ? at : null);
      onRawChange(event.target.value);
    },
    onSelect: (event: React.SyntheticEvent<HTMLInputElement>) => syncCaret(event.currentTarget),
    onClick: () => setDismissed(false),
    onFocus: () => setFocused(true),
    onBlur: () => {
      setFocused(false);
      setCalendarFor(null);
      setTypedAt(null);
    },
  };

  return {
    tokens,
    context,
    suggestions,
    focused,
    open,
    activeIndex,
    calendarOpen,
    /** The open value list is one page of more matches (not an option). */
    hasMore: context?.kind === "value" && hasMore,
    committedRaw,
    anchorIndex: context?.token?.from ?? caret,
    accept,
    acceptRange,
    dismiss: () => setDismissed(true),
    inputProps,
  };
}
